import { Fragment, useEffect, useState } from "react";

import { Spinner } from "@blueprintjs/core";
import {
  ChevronRight,
  LucideProps,
  MonitorUp,
  MonitorX,
  Play,
} from "lucide-react";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { Popup } from "@/components/pieces/popup/Popup";
import { formatBytes } from "@/components/pieces/roleplays/utils";
import { Button, ButtonIntent, ButtonVariant } from "@/components/ui/button";
import { formatFileType } from "@/components/ui/file-list";
import { PDFViewer } from "@/components/ui/pdf-viewer";
import { usePresentationsPickerQuery } from "@/graphql";
import { FullScenario } from "@/types/roleplays";

type Presentation = FullScenario["presentations"][number];

export function PresentationsPicker(props: {
  scenarioId: string;
  selectedPresentation: Presentation | null;
  presentations: Presentation[];
  onPresentationChange: (presentation: Presentation | null) => void;
  isScreenSharing: boolean;
  enableScreenSharing: boolean;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => void;
}) {
  // If only screen sharing is enabled, then this button
  // should directly trigger start/stop screen share
  // without opening the popup
  if (props.enableScreenSharing && props.presentations.length === 0) {
    return (
      <Tooltip
        content={props.isScreenSharing ? "Stop Screen Sharing" : "Share Screen"}
        placement="top"
      >
        <Button
          intent={props.isScreenSharing ? "danger" : "primary"}
          variant="outline"
          size="lg"
          onClick={
            props.isScreenSharing
              ? props.stopScreenShare
              : props.startScreenShare
          }
        >
          <div className="grid grid-flow-col items-center">
            {props.isScreenSharing ? (
              <MonitorX size={24} />
            ) : (
              <MonitorUp size={24} />
            )}
          </div>
        </Button>
      </Tooltip>
    );
  }

  const [showPopup, setShowPopup] = useState(false);
  const isPresenting =
    Boolean(props.selectedPresentation) || props.isScreenSharing;

  const button = (
    <Button
      intent={isPresenting ? "primary" : "secondary"}
      variant={isPresenting ? "outline" : "solid"}
      size="lg"
      style={{
        backgroundColor: isPresenting ? "bg-blue-200" : undefined,
      }}
      onClick={() => setShowPopup(!showPopup)}
    >
      <div className="grid grid-flow-col items-center gap-1">
        <MonitorUp size={24} />
        <ChevronRight size={16} />
      </div>
    </Button>
  );

  const popupContent = (
    <div className="grid w-68 mt-3">
      <div className="text-xs text-gray-500 mb-2 font-semibold mx-4">
        Pick a deck to present
      </div>
      <div className="grid w-full">
        {props.presentations.map((presentation, index) => {
          return (
            <Fragment key={presentation.file.id}>
              <PresentationPickerItem
                scenarioId={props.scenarioId}
                selected={
                  props.selectedPresentation?.file.id === presentation.file.id
                }
                presentation={presentation}
                onClick={(presentation) => {
                  setShowPopup(false);
                  props.stopScreenShare();
                  props.onPresentationChange(presentation);
                }}
              />
              {index >= props.presentations.length - 1 ? null : (
                <div className="h-px bg-neutral-300" />
              )}
            </Fragment>
          );
        })}
      </div>
      {(props.enableScreenSharing || props.selectedPresentation) && (
        <div className="grid p-4 gap-4">
          {props.enableScreenSharing &&
            (props.isScreenSharing ? (
              <ShowOrStopButton
                intent="danger"
                icon={MonitorX}
                text="Stop Screen Sharing"
                onClick={async () => {
                  props.stopScreenShare();
                  setShowPopup(false);
                }}
              />
            ) : (
              <ShowOrStopButton
                intent="primary"
                icon={MonitorUp}
                text="Share Screen"
                onClick={async () => {
                  await props.startScreenShare();
                  props.onPresentationChange(null);
                  setShowPopup(false);
                }}
              />
            ))}
          {props.selectedPresentation && (
            <ShowOrStopButton
              intent="danger"
              icon={MonitorX}
              text="Stop Presenting"
              onClick={() => {
                setShowPopup(false);
                props.onPresentationChange(null);
              }}
            />
          )}
        </div>
      )}
    </div>
  );

  return (
    <Popup
      popupContent={popupContent}
      show={showPopup}
      position="top"
      onDismiss={() => setShowPopup(false)}
    >
      {button}
    </Popup>
  );
}

export const ShowOrStopButton = (props: {
  icon: React.ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
  >;
  onClick: () => void;
  text: string;
  intent: ButtonIntent;
  variant?: ButtonVariant;
}) => {
  return (
    <Button
      onClick={props.onClick}
      intent={props.intent}
      variant={props.variant ?? "outline"}
    >
      <div className="grid w-full grid-flow-col gap-2 items-center justify-center">
        <props.icon size={16} />
        <div>{props.text}</div>
      </div>
    </Button>
  );
};

const PresentationPickerItem = (props: {
  scenarioId: string;
  presentation: Presentation;
  selected: boolean;
  onClick: (presentation: Presentation) => void;
}) => {
  const { presentation } = props;
  const query = usePresentationsPickerQuery({
    variables: {
      scenarioId: props.scenarioId,
      fileId: presentation.file.id,
    },
  });

  const isTranscribing = query.data?.scenarioPresentation?.isTranscribing;
  useEffect(() => {
    if (isTranscribing) {
      query.startPolling(1000);
    } else {
      query.stopPolling();
    }
    return () => query.stopPolling();
  }, [isTranscribing]);
  const pageDescriptions =
    query.data?.scenarioPresentation?.pageDescriptions ?? [];
  const progress = Math.round(
    (pageDescriptions.filter((description) => description !== "").length /
      pageDescriptions.length) *
      100,
  );
  return (
    <div className="grid">
      <div
        className={`row-1 col-1 ${isTranscribing ? "pointer-events-none opacity-25" : ""}`}
      >
        <Tooltip
          className="grid w-full"
          content={presentation.file.name}
          placement="right"
        >
          <div
            className={[
              "grid grid-cols-[auto_1fr_auto] items-center gap-[6px] cursor-pointer hover:bg-blue-100 active:bg-blue-200 py-[8px] px-[16px]",
              props.selected ? "bg-blue-200" : "",
            ].join(" ")}
            onClick={() =>
              props.onClick({
                ...presentation,
                pageDescriptions,
              })
            }
          >
            <div className="grid overflow-hidden items-center border border-solid border-gray-300 items-center w-[60px] h-[48px] rounded-xs bg-gray-200">
              {presentation.file.url && (
                <PDFViewer pageNumber={1} url={presentation.file.url} />
              )}
            </div>
            <div className="grid">
              <div className="text-[12px] text-main truncate">
                {presentation.file.name}
              </div>
              <div className="text-[10px] text-gray-400">
                {formatFileType(presentation.file.contentType ?? "")} ·{" "}
                {formatBytes(presentation.file.size)}
              </div>
            </div>
            <Play size={16} color="#3788EE" />
          </div>
        </Tooltip>
      </div>
      {isTranscribing && (
        <>
          <div className="row-1 col-1 grid items-center justify-center bg-blue-100 text-sm">
            {progress}%
          </div>
          <div className="row-1 col-1 grid items-center cursor-arrow">
            <Tooltip content="Transcribing..." placement="right">
              <Spinner />
            </Tooltip>
          </div>
        </>
      )}
    </div>
  );
};
